import React from "react";
import axios from "axios";
import {
  addDays,
  addMinutes,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import "./CalendlyBookingDemo.css";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import TeacherDriveTimeBubble from "../AdminWorkflows/TempTeacherDistancePopup/TeacherDriveTimeBubble/TeacherDriveTimeBubble";
import { createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime } from "../Calendar/calendarFunctions";

const defaultFamilyName = "Anderson Family";
const defaultStudents = ["Mia", "Noah", "Liam"];

const availabilitySections = ["Morning", "Afternoon", "Evening"];
const SLOT_MINUTES = 15;

const getSuggestionTeacherId = (suggestion) => {
  const obj = suggestion?.eventOrTeacherObject || suggestion;
  return (
    obj?.teacherId ||
    obj?.TeacherID ||
    obj?.teacherID ||
    obj?.TeacherId ||
    obj?.id ||
    null
  );
};

const getAvailabilityDetailsFromSuggestion = (suggestion) => {
  const obj = suggestion?.eventOrTeacherObject || suggestion;
  return (
    obj?.AvailabilityDetails ||
    obj?.availabilityDetails ||
    obj?.availability ||
    obj?.Availability ||
    null
  );
};

const dayEntryHasAvailability = (slots, depth = 0) => {
  if (!slots) return false;
  if (Array.isArray(slots)) {
    return slots.length > 0;
  }
  if (typeof slots === "object") {
    if (depth > 2) {
      return Object.keys(slots).length > 0;
    }
    return Object.values(slots).some((value) =>
      dayEntryHasAvailability(value, depth + 1)
    );
  }
  return Boolean(slots);
};

const collectAvailabilityDays = (targetSet, availabilityDetails) => {
  if (!availabilityDetails || typeof availabilityDetails !== "object") return;
  Object.entries(availabilityDetails).forEach(([day, slots]) => {
    if (!day) return;
    if (dayEntryHasAvailability(slots)) {
      targetSet.add(day.toString().toLowerCase());
    }
  });
};

const parseTimeToMinutes = (value) => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const formats = ["h:mm a", "h a", "hh:mm a", "H:mm", "HH:mm"];
  for (const fmt of formats) {
    try {
      const parsedDate = parse(trimmed, fmt, new Date());
      if (!isNaN(parsedDate?.getTime?.())) {
        return parsedDate.getHours() * 60 + parsedDate.getMinutes();
      }
    } catch (error) {
      // ignore parsing failure for this format
    }
  }
  const dateIso = new Date(`1970-01-01T${trimmed}`);
  if (!isNaN(dateIso.getTime())) {
    return dateIso.getHours() * 60 + dateIso.getMinutes();
  }
  const dateSpace = new Date(`1970-01-01 ${trimmed}`);
  if (!isNaN(dateSpace.getTime())) {
    return dateSpace.getHours() * 60 + dateSpace.getMinutes();
  }
  const numeric = Number(trimmed);
  if (Number.isFinite(numeric)) return numeric;
  return null;
};

const getDayAvailabilityFromDetails = (availabilityDetails, targetDayName) => {
  if (!availabilityDetails) return null;
  if (Array.isArray(availabilityDetails)) return availabilityDetails;
  const normalizedDay = targetDayName.toLowerCase();
  for (const [key, value] of Object.entries(availabilityDetails)) {
    if (!key) continue;
    const keyLower = key.toLowerCase();
    if (
      keyLower === normalizedDay ||
      keyLower.slice(0, 3) === normalizedDay.slice(0, 3)
    ) {
      return value;
    }
  }
  return null;
};

const readTimeValue = (window, type) => {
  if (!window) return null;
  const lowerType = type === "end" ? "end" : "start";
  const candidateKeys =
    lowerType === "start"
      ? ["start", "Start", "startTime", "StartTime", "from", "From", "timeStart", "time_start", "begin", "Begin", "beginTime"]
      : ["end", "End", "endTime", "EndTime", "to", "To", "timeEnd", "time_end", "finish", "Finish", "finishTime"];
  for (const key of candidateKeys) {
    if (window[key]) return window[key];
  }
  const minutesKey = lowerType === "start" ? ["startMinutes", "start_minute"] : ["endMinutes", "end_minute"];
  for (const key of minutesKey) {
    if (window[key] != null) return window[key];
  }
  if (typeof window === "string") {
    const pieces = window.split("-");
    if (pieces.length >= 2) {
      return lowerType === "start" ? pieces[0] : pieces[pieces.length - 1];
    }
  }
  return null;
};

const buildTeacherTimeSlots = ({
  availability,
  selectedDate,
  teacherName,
  teacherId,
  teacherImage,
  busyIntervals = [],
  slotMinutes = SLOT_MINUTES,
}) => {
  if (!Array.isArray(availability) || !selectedDate) return [];
  const startOfSelectedDay = startOfDay(selectedDate);
  const initials = getInitials(teacherName);
  const firstName = teacherName?.split(" ")[0] || teacherName || "Teacher";
  const slots = [];
  availability.forEach((window, windowIndex) => {
    const startValue = readTimeValue(window, "start");
    const endValue = readTimeValue(window, "end");
    const startMinutes = parseTimeToMinutes(startValue);
    const endMinutes = parseTimeToMinutes(endValue);
    if (
      !Number.isFinite(startMinutes) ||
      !Number.isFinite(endMinutes) ||
      endMinutes <= startMinutes
    ) {
      return;
    }
    for (let minute = startMinutes; minute < endMinutes; minute += slotMinutes) {
      const slotEnd = Math.min(minute + slotMinutes, endMinutes);
      if (
        isIntervalOverlapping(minute, slotEnd, busyIntervals)
      ) {
        continue;
      }
      const slotDate = addMinutes(startOfSelectedDay, minute);
      slots.push({
        id: `${teacherId || teacherName || "teacher"}-${minute}-${windowIndex}`,
        timeLabel: format(slotDate, "h:mm a"),
        teacherName: teacherName || "Teacher",
        teacherFirstName: firstName,
        teacherInitials: initials,
        startMinutes: minute,
        teacherImage,
        duration: slotMinutes,
      });
    }
  });
  return slots;
};

const getInitials = (name = "") => {
  if (!name) return "T";
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .join("")
    .slice(0, 2) || "T";
};

const resolveImageUrl = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("http")) return trimmed;
  return `https://app.atharo.app/images/${trimmed}`;
};

const getTeacherImageFromSuggestion = (suggestion) => {
  const obj = suggestion?.eventOrTeacherObject || suggestion;
  const imageRaw =
    obj?.imageUrl ||
    obj?.teacherImage ||
    obj?.TeacherImage ||
    obj?.Image ||
    obj?.ImageName ||
    suggestion?.imageUrl ||
    suggestion?.teacherImage;
  return resolveImageUrl(imageRaw);
};

const normalizeStudentKey = (value) =>
  value
    ? value.toString().trim().toLowerCase().replace(/[^a-z0-9]/g, "")
    : "";

const getStudentKeyFromRecord = (student, fallbackName = "") => {
  if (!student) return normalizeStudentKey(fallbackName);
  const sources = [
    student.studentId,
    student.id,
    student._id,
    student.studentName,
    student.name,
    student.firstName,
    student.displayName,
    student.label,
    fallbackName,
  ];
  for (const source of sources) {
    const normalized = normalizeStudentKey(source);
    if (normalized) return normalized;
  }
  return "";
};

const buildStudentLessonsMap = (events = []) => {
  const map = {};
  events.forEach((event) => {
    if (!event || event.isPendingOffer) return;
    const date = parseDateTimeValue(
      event?.dateTime ||
        event?.startDateTime ||
        event?.start ||
        event?.startISO ||
        event?.startDate
    );
    if (!date) return;
    const key =
      normalizeStudentKey(event?.studentId) ||
      normalizeStudentKey(event?.studentName);
    if (!key) return;
    if (!map[key]) map[key] = [];
    map[key].push({
      day: format(date, "EEE"),
      time: format(date, "h:mm a"),
      timestamp: date.getTime(),
    });
  });
  Object.keys(map).forEach((key) => {
    map[key].sort((a, b) => a.timestamp - b.timestamp);
    map[key] = map[key]
      .slice(0, 3)
      .map(({ day, time }) => ({ day, time }));
  });
  return map;
};

const parseDateTimeValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return isNaN(parsed) ? null : parsed;
};

const extractBusyIntervals = (calendarDays) => {
  const result = {};
  if (!calendarDays) return result;
  Object.entries(calendarDays).forEach(([dateKey, value]) => {
    const events = value?.events || [];
    const intervals = events
      .map((event) => {
        const startDate = parseDateTimeValue(event?.dateTime);
        if (!startDate) return null;
        const startMinutes =
          startDate.getHours() * 60 + startDate.getMinutes();
        const lessonLength = Number(event?.lessonLength) || 30;
        return { start: startMinutes, end: startMinutes + lessonLength };
      })
      .filter(Boolean);
    if (intervals.length) {
      intervals.sort((a, b) => a.start - b.start);
      result[dateKey] = intervals;
    }
  });
  return result;
};

const isIntervalOverlapping = (start, end, intervals = []) => {
  if (!intervals.length) return false;
  return intervals.some(
    (interval) => Math.max(start, interval.start) < Math.min(end, interval.end)
  );
};

const isSlotAdjacentToBusy = (start, duration, intervals = []) => {
  if (!intervals.length || !Number.isFinite(start) || !Number.isFinite(duration)) {
    return false;
  }
  const slotEnd = start + duration;
  return intervals.some((interval) => {
    const beforeDiff = Math.abs(slotEnd - interval.start);
    const afterDiff = Math.abs(start - interval.end);
    return beforeDiff <= 1 || afterDiff <= 1;
  });
};
const getSuggestionDateDetails = (suggestion) => {
  const obj = suggestion?.eventOrTeacherObject || suggestion || {};
  const dateCandidates = [
    obj?.dateTime,
    obj?.startISO,
    obj?.start,
    obj?.startDate,
    obj?.Date,
    obj?.date,
    suggestion?.dateTime,
    suggestion?.startISO,
  ];
  for (const candidate of dateCandidates) {
    if (!candidate) continue;
    const parsed = new Date(candidate);
    if (!isNaN(parsed)) {
      return {
        date: parsed,
        dateKey: format(parsed, "yyyy-MM-dd"),
        startMinutes: parsed.getHours() * 60 + parsed.getMinutes(),
        timeLabel: format(parsed, "h:mm a"),
      };
    }
  }
  if (obj?.startUnix) {
    const parsed = new Date(Number(obj.startUnix) * 1000);
    if (!isNaN(parsed)) {
      return {
        date: parsed,
        dateKey: format(parsed, "yyyy-MM-dd"),
        startMinutes: parsed.getHours() * 60 + parsed.getMinutes(),
        timeLabel: format(parsed, "h:mm a"),
      };
    }
  }
  return null;
};

const StudentChip = ({ student }) => {
  return (
    <div className="calendly-demo__student-card">
      <div className="calendly-demo__student-card-header">
        <div className="calendly-demo__student-card-avatar">
          {(student.displayName || student.label)
            .split(" ")
            .map((part) => part[0])
            .join("")
            .slice(0, 2)}
        </div>
        <div className="calendly-demo__student-card-title">
          <div>{student.displayName || student.label}</div>
          {student.detail && (
            <div className="calendly-demo__student-card-detail">
              {student.detail}
            </div>
          )}
        </div>
      </div>
      {student.lessonTimes?.length ? (
        <div className="calendly-demo__student-card-lessons">
          {student.lessonTimes.map((lesson) => (
            <div
              className="calendly-demo__student-card-lesson"
              key={`${lesson.day}-${lesson.time}`}
            >
              <span>{lesson.day}</span>
              <span>{lesson.time}</span>
            </div>
          ))}
        </div>
      ) : null}
      {student.instrumentList?.length > 0 && (
        <div className="calendly-demo__student-card-tags">
          {student.instrumentList.map((instrument) => (
            <span className="calendly-demo__student-card-tag" key={instrument}>
              {instrument}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const CalendlyBookingDemo = ({
  driveTimeSuggestions = [],
  onClose,
  familyId = null,
  familyName = defaultFamilyName,
  students = defaultStudents,
  availability = null,
  familyMessage = "",
}) => {
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [selectedDate, setSelectedDate] = React.useState(new Date());
  const [selectedTime, setSelectedTime] = React.useState(null);
  const [teacherAvailabilityMap, setTeacherAvailabilityMap] = React.useState({});
  const [teacherCalendarEvents, setTeacherCalendarEvents] = React.useState({});
  const [familyLessonMap, setFamilyLessonMap] = React.useState({});
  const today = startOfDay(new Date());

  const monthLabel = format(currentMonth, "MMMM yyyy");

  const calendarCells = React.useMemo(() => {
    const startDate = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
    const endDate = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
    const rows = [];
    let day = startDate;

    while (day <= endDate) {
      const days = [];
      for (let i = 0; i < 7; i++) {
        days.push(day);
        day = addDays(day, 1);
      }
      rows.push(days);
    }
    return rows;
  }, [currentMonth]);

  const timezoneLabel = `${Intl.DateTimeFormat().resolvedOptions().timeZone}`;

  React.useEffect(() => {
    const missingIds = new Set();
    driveTimeSuggestions.forEach((suggestion) => {
      const teacherId = getSuggestionTeacherId(suggestion);
      if (!teacherId) return;
      const key = teacherId.toString().toLowerCase();
      const availabilityDetails = getAvailabilityDetailsFromSuggestion(suggestion);
      if (!availabilityDetails && !teacherAvailabilityMap[key]) {
        missingIds.add(key);
      }
    });
    if (!missingIds.size) return;
    let cancelled = false;
    const loadTeachers = async () => {
      try {
        const response = await axios.get(`${baseUrl}/initialadminrequest`, getRequestHeaders());
        if (cancelled) return;
        const map = {};
        const list = response?.data || [];
        list.forEach((teacher) => {
          const rawId = teacher?.TeacherID || teacher?.teacherId || teacher?._id;
          if (!rawId) return;
          const key = rawId.toString().toLowerCase();
          if (!missingIds.has(key)) return;
          map[key] = teacher?.AvailabilityDetails || teacher?.Availability;
        });
        if (Object.keys(map).length) {
          setTeacherAvailabilityMap((prev) => ({ ...prev, ...map }));
        }
      } catch (error) {
        console.error("[booking-demo] unable to load teacher availability", error);
      }
    };
    loadTeachers();
    return () => {
      cancelled = true;
    };
  }, [driveTimeSuggestions, teacherAvailabilityMap]);

  const calendarRangeDays = React.useMemo(() => {
    const startDate = startOfWeek(startOfMonth(currentMonth), {
      weekStartsOn: 0,
    });
    const endDate = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentMonth]);

  React.useEffect(() => {
    const missing = new Map();
    driveTimeSuggestions.forEach((suggestion) => {
      const teacherId = getSuggestionTeacherId(suggestion);
      if (!teacherId) return;
      const key = teacherId.toString().toLowerCase();
      if (!teacherCalendarEvents[key] && !missing.has(key)) {
        missing.set(key, teacherId);
      }
    });
    if (!missing.size) return;
    let cancelled = false;
    const loadCalendars = async () => {
      try {
        const responses = await Promise.all(
          Array.from(missing.entries()).map(async ([key, teacherId]) => {
            try {
              const response = await axios.get(
                `${baseUrl}/calendar/teacher/${teacherId}`,
                getRequestHeaders()
              );
              return [key, response?.data || []];
            } catch (error) {
              console.error("[booking-demo] unable to load teacher calendar", {
                teacherId,
                error,
              });
              return [key, []];
            }
          })
        );
        if (cancelled) return;
        const updates = {};
        responses.forEach(([key, events]) => {
          updates[key] = events;
        });
        if (Object.keys(updates).length) {
          setTeacherCalendarEvents((prev) => ({ ...prev, ...updates }));
        }
      } catch (error) {
        console.error("[booking-demo] calendar fetch failed", error);
      }
    };
    loadCalendars();
    return () => {
      cancelled = true;
    };
  }, [driveTimeSuggestions, teacherCalendarEvents]);

  const teacherBusyMap = React.useMemo(() => {
    const map = {};
    Object.entries(teacherCalendarEvents).forEach(([key, events]) => {
      if (!Array.isArray(events) || !events.length) return;
      let calendarDays = {};
      createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
        calendarRangeDays,
        events,
        (value) => {
          calendarDays = value || {};
        }
      );
      map[key] = extractBusyIntervals(calendarDays);
    });
    return map;
  }, [teacherCalendarEvents, calendarRangeDays]);

  React.useEffect(() => {
    if (!familyId) {
      setFamilyLessonMap({});
      return;
    }
    let cancelled = false;
    const loadFamilyLessons = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/calendar/family/${familyId}`,
          getRequestHeaders()
        );
        if (cancelled) return;
        const events = Array.isArray(response?.data) ? response.data : [];
        setFamilyLessonMap(buildStudentLessonsMap(events));
      } catch (error) {
        console.error("[booking-demo] unable to load family calendar", error);
        if (!cancelled) {
          setFamilyLessonMap({});
        }
      }
    };
    loadFamilyLessons();
    return () => {
      cancelled = true;
    };
  }, [familyId]);

  const teacherAvailabilityDays = React.useMemo(() => {
    const daySet = new Set();
    driveTimeSuggestions.forEach((suggestion) => {
      const availabilityDetails = getAvailabilityDetailsFromSuggestion(suggestion);
      if (availabilityDetails) {
        collectAvailabilityDays(daySet, availabilityDetails);
        return;
      }
      const teacherId = getSuggestionTeacherId(suggestion);
      if (!teacherId) return;
      const key = teacherId.toString().toLowerCase();
      const fetchedAvailability = teacherAvailabilityMap[key];
      if (fetchedAvailability) {
        collectAvailabilityDays(daySet, fetchedAvailability);
      }
    });
    return daySet;
  }, [driveTimeSuggestions, teacherAvailabilityMap]);

  const availableTimeSlots = React.useMemo(() => {
    if (!selectedDate) return [];
    const selectedDayKey = format(selectedDate, "EEEE").toLowerCase();
    const selectedDateKey = format(selectedDate, "yyyy-MM-dd");
    const startOfSelectedDay = startOfDay(selectedDate);
    const slots = [];
    const seenSlotKeys = new Set();
    const addSlot = (slot) => {
      if (!slot) return;
      const slotKey = `${slot.teacherName || ""}-${slot.timeLabel || ""}-${slot.startMinutes || ""}`;
      if (seenSlotKeys.has(slotKey)) return;
      seenSlotKeys.add(slotKey);
      slots.push(slot);
    };
    driveTimeSuggestions.forEach((suggestion, index) => {
      const teacherObj = suggestion?.eventOrTeacherObject || suggestion;
      const wantsMore =
        teacherObj?.wantsMoreStudents ??
        teacherObj?.WantsMoreStudents ??
        teacherObj?.wantsMore ??
        teacherObj?.moreStudents;
      if (wantsMore === false || wantsMore === "false") {
        return;
      }
      const teacherName =
        teacherObj?.name ||
        suggestion?.teacherName ||
        `Teacher ${index + 1}`;
      const teacherId = getSuggestionTeacherId(suggestion) || `teacher-${index}`;
      const teacherImage = getTeacherImageFromSuggestion(suggestion);
      const teacherInitials = getInitials(teacherName);
      const teacherFirstName = teacherName?.split(" ")[0] || teacherName || "Teacher";
      const teacherKey = teacherId?.toString().toLowerCase();
      const busyIntervalsForDate =
        (teacherKey && teacherBusyMap[teacherKey]?.[selectedDateKey]) || [];
      const limitToAdjacentSlots = busyIntervalsForDate.length > 0;
      const dateDetails = getSuggestionDateDetails(suggestion);
      if (dateDetails) {
        if (dateDetails.dateKey !== selectedDateKey) {
          return;
        }
        const explicitTime =
          dateDetails.startMinutes ??
          parseTimeToMinutes(suggestion?.eventOrTeacherObject?.time);
        const slotEnd = Number.isFinite(explicitTime)
          ? explicitTime + SLOT_MINUTES
          : null;
        if (
          Number.isFinite(explicitTime) &&
          isIntervalOverlapping(explicitTime, slotEnd, busyIntervalsForDate)
        ) {
          return;
        }
        if (
          limitToAdjacentSlots &&
          !isSlotAdjacentToBusy(explicitTime, SLOT_MINUTES, busyIntervalsForDate)
        ) {
          return;
        }
        const slotLabel =
          dateDetails.timeLabel ||
          (Number.isFinite(explicitTime)
            ? format(addMinutes(startOfSelectedDay, explicitTime), "h:mm a")
            : suggestion?.eventOrTeacherObject?.time || "Time TBD");
        addSlot({
          id: `${teacherId || teacherName || "teacher"}-event-${dateDetails.dateKey}-${index}`,
          timeLabel: slotLabel,
          teacherName,
          teacherFirstName,
          teacherInitials,
          teacherImage,
          startMinutes: Number.isFinite(explicitTime)
            ? explicitTime
            : 9999 + slots.length,
        });
        return;
      }
      const suggestionAvailability = getAvailabilityDetailsFromSuggestion(suggestion);
      let dayAvailability = getDayAvailabilityFromDetails(
        suggestionAvailability,
        selectedDayKey
      );
      if (!dayAvailability) {
        const key = teacherId?.toString().toLowerCase();
        if (key && teacherAvailabilityMap[key]) {
          dayAvailability = getDayAvailabilityFromDetails(
            teacherAvailabilityMap[key],
            selectedDayKey
          );
        }
      }
      if (!dayAvailability) return;
      const normalizedAvailability = Array.isArray(dayAvailability)
        ? dayAvailability
        : Array.isArray(dayAvailability?.slots)
        ? dayAvailability.slots
        : dayAvailability && typeof dayAvailability === "object"
        ? [dayAvailability]
        : [];
      const teacherSlots = buildTeacherTimeSlots({
        availability: normalizedAvailability,
        selectedDate,
        teacherName,
        teacherId,
        teacherImage,
        busyIntervals: busyIntervalsForDate,
        slotMinutes: SLOT_MINUTES,
      });
      if (teacherSlots.length) {
        const slotsToAdd = limitToAdjacentSlots
          ? teacherSlots.filter((slot) =>
              isSlotAdjacentToBusy(
                slot.startMinutes,
                slot.duration || SLOT_MINUTES,
                busyIntervalsForDate
              )
            )
          : teacherSlots;
        slotsToAdd.forEach(addSlot);
        return;
      }
      const suggestionDayRaw =
        suggestion?.eventOrTeacherObject?.day ||
        suggestion?.day ||
        suggestion?.eventOrTeacherObject?.weekday;
      if (
        suggestionDayRaw &&
        suggestionDayRaw.toString().toLowerCase().slice(0, 3) ===
          selectedDayKey.slice(0, 3)
      ) {
        const manualMinutes = parseTimeToMinutes(
          suggestion?.eventOrTeacherObject?.time || suggestion?.time
        );
        if (
          Number.isFinite(manualMinutes) &&
          isIntervalOverlapping(
            manualMinutes,
            manualMinutes + SLOT_MINUTES,
            busyIntervalsForDate
          )
        ) {
          return;
        }
        if (
          limitToAdjacentSlots &&
          (!Number.isFinite(manualMinutes) ||
            !isSlotAdjacentToBusy(
              manualMinutes,
              SLOT_MINUTES,
              busyIntervalsForDate
            ))
        ) {
          return;
        }
        const manualLabel = suggestion?.eventOrTeacherObject?.time
          ? suggestion.eventOrTeacherObject.time
          : manualMinutes != null
          ? format(addMinutes(startOfSelectedDay, manualMinutes), "h:mm a")
          : "Time TBD";
        addSlot({
          id: `${teacherId || teacherName || "teacher"}-day-${selectedDayKey}-${index}`,
          timeLabel: manualLabel,
          teacherName,
          teacherFirstName,
          teacherInitials,
          teacherImage,
          startMinutes: Number.isFinite(manualMinutes)
            ? manualMinutes
            : 9999 + slots.length,
        });
      }
    });
    slots.sort((a, b) => {
      if (a.startMinutes === b.startMinutes) {
        return a.teacherName.localeCompare(b.teacherName);
      }
      return a.startMinutes - b.startMinutes;
    });
    return slots;
  }, [driveTimeSuggestions, teacherAvailabilityMap, teacherBusyMap, selectedDate]);

  React.useEffect(() => {
    if (!selectedTime) return;
    const stillValid = availableTimeSlots.some((slot) => slot.id === selectedTime);
    if (!stillValid) {
      setSelectedTime(null);
    }
  }, [availableTimeSlots, selectedTime]);

  const suggestionMatches =
    Array.isArray(driveTimeSuggestions) && driveTimeSuggestions.length > 0
      ? driveTimeSuggestions
      : null;

  const studentList =
    Array.isArray(students) && students.length
      ? students.map((student, index) => {
          if (typeof student === "string") {
            const key = normalizeStudentKey(student);
            return {
              label: student,
              lessonTimes: familyLessonMap[key] || [],
            };
          }
          const name =
            student?.studentName ||
            student?.name ||
            student?.firstName ||
            student?.StudentName ||
            `Student ${index + 1}`;
          const instrumentList =
            student?.instrumentList?.map((inst) => inst.name) ||
            student?.InstrumentList?.map((inst) => inst.name) ||
            student?.instruments ||
            [];
          const instrument =
            instrumentList.length > 0
              ? instrumentList[0]
              : student?.instrument ||
                student?.Instrument ||
                student?.primaryInstrument ||
                "";
          const age =
            student?.age ||
            student?.Age ||
            student?.yearsOld ||
            student?.studentAge ||
            null;
          const ageLabel = age ? `${age} years old` : "";
          const lessonLength =
            student?.lessonLength ||
            student?.LessonLength ||
            student?.lesson_duration ||
            student?.lesson?.lessonLength ||
            null;
          const lessonLengthLabel = lessonLength ? `${lessonLength} min` : "";
          const instrumentLabel = instrument ? instrument : "";
          const detailLabel = [ageLabel, lessonLengthLabel, instrumentLabel]
            .filter(Boolean)
            .join(" • ");
          const studentKey = getStudentKeyFromRecord(student, name);
          return {
            label: name,
            detail: detailLabel,
            instrumentList: instrumentList,
            displayName: name,
            lessonTimes: familyLessonMap[studentKey] || [],
          };
        })
      : defaultStudents.map((name) => ({ label: name, displayName: name, lessonTimes: [] }));

  const displayFamilyName = familyName || defaultFamilyName;

  return (
    <div className="calendly-demo">
      {onClose && (
        <button className="calendly-demo__close" onClick={onClose} type="button">
          ×
        </button>
      )}
      <div className="calendly-demo__panel calendly-demo__details">
        <p className="calendly-demo__family">{displayFamilyName}</p>
        <div className="calendly-demo__students">
          {studentList.map((student) => (
            <StudentChip student={student} key={student.label} />
          ))}
        </div>
        <p className="calendly-demo__description">
          {familyMessage || "Select a time that works best for this family and confirm the lesson immediately."}
        </p>
        {availability && (
          <div className="calendly-demo__availability">
            <div className="calendly-demo__availability-title">Availability</div>
            {Object.entries(availability).map(([day, slots]) => {
              const hasAvailability =
                slots?.Morning || slots?.Afternoon || slots?.Evening;
              if (!hasAvailability) return null;
              return (
                <div className="calendly-demo__availability-row" key={day}>
                  <div className="calendly-demo__availability-day">{day.slice(0, 3)}</div>
                  <div className="calendly-demo__availability-times">
                    {availabilitySections.map((section) => (
                      <div className="calendly-demo__availability-slot" key={section}>
                        <span>{section}</span>
                        <div
                          className={`calendly-demo__availability-indicator ${
                            slots[section] ? "is-available" : ""
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="calendly-demo__panel calendly-demo__calendar-area">
        <div className="calendly-demo__calendar">
          <div className="calendly-demo__calendar-header">
            <div className="calendly-demo__month-label">{monthLabel}</div>
            <div className="calendly-demo__calendar-controls">
              <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                ‹
              </button>
              <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                ›
              </button>
            </div>
          </div>
          <h2 className="calendly-demo__calendar-title">Select a Date & Time</h2>
          <div className="calendly-demo__calendar-grid">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((dow) => (
              <div key={dow} className="calendly-demo__calendar-day-label">
                {dow}
              </div>
            ))}
            {calendarCells.flat().map((day) => {
              const isCurrent = isSameMonth(day, currentMonth);
              const isSelected = isSameDay(day, selectedDate);
              const dayName = format(day, "EEEE").toLowerCase();
              const hasTeacherAvailability = teacherAvailabilityDays.has(dayName);
              const isPastDay = day < today;
              return (
                <button
                  key={day.toISOString()}
                  className={`calendly-demo__calendar-cell ${
                    !isCurrent ? "is-outside" : ""
                  } ${isSelected ? "is-selected" : ""} ${
                    hasTeacherAvailability ? "has-availability" : ""
                  } ${isPastDay ? "is-past" : ""}`}
                  onClick={() => {
                    if (!isPastDay) {
                      setSelectedDate(day);
                    }
                  }}
                  disabled={!isCurrent || isPastDay}
                >
                  {format(day, "d")}
                </button>
              );
            })}
          </div>
          <div className="calendly-demo__timezone">Times shown in {timezoneLabel}</div>
        </div>

        <div className="calendly-demo__times">
          <div className="calendly-demo__times-header">
            {format(selectedDate, "EEEE, MMMM d")}
          </div>
          <div className="calendly-demo__times-list">
            {availableTimeSlots.length ? (
              availableTimeSlots.map((slot) => {
                const isSelected = selectedTime === slot.id;
                return (
                  <div
                    key={slot.id}
                    className={`calendly-demo__time-row ${
                      isSelected ? "is-selected" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className={`calendly-demo__time-slot ${
                        isSelected ? "is-selected" : ""
                      }`}
                      onClick={() => setSelectedTime(slot.id)}
                    >
                      <span className={`calendly-demo__time-avatar ${isSelected ? "is-hidden" : ""}`}>
                        {slot.teacherImage ? (
                          <img src={slot.teacherImage} alt={slot.teacherName} />
                        ) : (
                          slot.teacherInitials
                        )}
                      </span>
                      <div className="calendly-demo__time-info">
                        <span className="calendly-demo__time-label">{slot.timeLabel}</span>
                        <span className="calendly-demo__time-teacher">{slot.teacherName}</span>
                      </div>
                    </button>
                    {isSelected && (
                      <button className="calendly-demo__confirm-button">
                        Confirm{" "}
                        <span className="calendly-demo__confirm-subtext">
                          lesson with {slot.teacherFirstName}
                        </span>
                      </button>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="calendly-demo__times-empty">
                No teacher availability for this date yet.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="calendly-demo__panel calendly-demo__suggestions">
        <div className="calendly-demo__suggestions-header">
          <h3>Drive Time Suggestions</h3>
          <p className="calendly-demo__suggestions-subtitle">
            Upcoming teachers near this family.
          </p>
        </div>
        <div className="calendly-demo__suggestions-body scheduling-tab-fall-2023__suggestions-wrapper">
          <div className="scheduling-tab-fall-2023__suggestions-label">SUGGESTIONS</div>
          {suggestionMatches ? (
            <div className="calendly-demo__suggestions-scroll scheduling-tab-fall-2023__suggestions-scroll-wrapper">
              {suggestionMatches.map((suggestion, index) => (
                <TeacherDriveTimeBubble
                  key={`drive-suggestion-${index}`}
                  suggestion={suggestion}
                  invitations={[]}
                  setSchedulingHelperTeacherId={() => {}}
                />
              ))}
            </div>
          ) : (
            <p className="calendly-demo__suggestions-empty">No drive time suggestions yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendlyBookingDemo;
